import { createAction } from 'redux-act'

import { FormError } from 'types/error'

import { METHODS, STATUS } from '../constants'
import { NotificationType, PaymentCard } from '../types/payments'
import {
  CitiesResponseItem,
  DistrictsResponseItem,
  AltIdGetItem,
  UserSettingsType,
  BranchResponseItem,
} from './types'

const { GET, CREATE, VERIFY, UPDATE, DELETE } = METHODS
const { LOADING, SUCCESS, FAILURE } = STATUS

const resendEmailConfirmationSuccess = createAction('resend email confirmation success')
const resendEmailConfirmationFail = createAction('resend email confirmation fail')
const resendEmailConfirmationReset = createAction('resend email confirmation reset')

const updateDigitalReceiptsPreferencesSuccess = createAction(
  'update digital receipts preferences success',
)
const updateDigitalReceiptsPreferencesFail = createAction(
  'update digital receipts preferences failure',
)

const updateNewsletterPreferencesSuccess = createAction('update newsletter preferences success')
const updateNewsletterPreferencesFail = createAction('update newsletter preferences failure')

const settingsUpdateMessageReset = createAction('settings update message reset')
const triggerSettingsUpdateSuccessMessage = createAction('trigger settings update message')
const triggerSettingsUpdateFailMessage = createAction('trigger settings update message')

const deactivatePayLaterStart = createAction('settings/deactivate pay later')
const deactivatePayLaterSuccess = createAction('settings/deactivate pay later success')
const deactivatePayLaterFail = createAction('settings/deactivate pay later fail')

const requestCustomerDataSuccess = createAction('settings/request customer data success')
const requestCustomerDataFail = createAction('settings/request customer data fail')
const changePasswordStart = createAction('settings/change password start')
const changePasswordSuccess = createAction('settings/change password success')
const changePasswordFail = createAction('settings/change password fail')
const changePasswordResetErrors = createAction('settings/change password reset errors')

const deleteAccountStart = createAction('settings/delete account start')
const deleteAccountSuccess = createAction('settings/delete account success')
const deleteAccountFail = createAction('settings/delete account fail')
const triggerSettingsDeleteFailMessage = createAction('trigger settings delete message')
const settingsDeleteMessageReset = createAction('settings delete message reset')

const updateUserDetailsStart = createAction('settings/update user details start')
const updateUserDetailsSuccess = createAction('settings/update user details success')
const updateUserDetailsFail = createAction('settings/update user details fail')

const resetCommunityNickname = createAction('settings/reset community nickname')
const updateCommunityNicknameStart = createAction('settings/update community nickname start')
const updateCommunityNicknameSuccess = createAction<{
  communityNickname?: string
  redirectUrl?: string
}>('settings/update community nickname success')
const updateCommunityNicknameFail = createAction('settings/update community nickname fail')

const getAltIdsStart = createAction('get alternative ids start')
const getAltIdsSuccess = createAction<AltIdGetItem[]>('get alternative ids success')
const getAltIdsFail = createAction<FormError[]>('get alternative ids fail')

const addAltIdStart = createAction('add alternative id start')
const addAltIdSuccess = createAction('add alternative id success')
const addAltIdFail = createAction<FormError[]>('add alternative id fail')

const deleteAltIdStart = createAction('delete alternative id start')
const deleteAltIdSuccess = createAction('delete alternative id success')
const deleteAltIdFail = createAction<FormError[]>('delete alternative id fail')

const triggerAccountAddSuccessMessage = createAction<string>('trigger bank account add message')
const triggerAccountAddFailMessage = createAction<string>('trigger bank account add message')

const setApiErrorState = createAction<boolean>('set api failure state')

const setSelectedProvince =
  createAction<{ province: string; provinceCode?: string }>('set selected province')

const setSelectedCity = createAction<string>('set selected city (town)')

const fetchCitiesStart = createAction('fetch Cities start')
const fetchCitiesSuccess =
  createAction<{ province: string; citiesList: CitiesResponseItem; provinceCode?: string }>(
    'fetch Cities success',
  )
const fetchCitiesFail = createAction<Error>('fetch Cities fail')

const fetchDistrictsStart = createAction('fetch Districts start')
const fetchDistrictsSuccess =
  createAction<{ city: string; districtsList: DistrictsResponseItem; cityCode?: string }>(
    'fetch Districts success',
  )
const fetchDistrictsFail = createAction<Error>('fetch Districts fail')

const fetchPostalCodeStart = createAction('fetch Postal Code start')
const fetchPostalCodeSuccess = createAction<string>('fetch Cities success')
const fetchPostalCodeFail = createAction<Error>('fetch PostalCode fail')

const addPhoneAsAltIdSuccess = createAction('add phone as alternative id success')
const removePhoneAsAltIdSuccess = createAction('remove phone as alternative id success')
const resetPhoneAsAltIdNotification = createAction('reset alt id notification message')

const addressBookAction = (method, status): any =>
  createAction(`settings/addressbook - ${method} - ${status}`, (payload) => ({
    data: payload,
    method,
  }))

const addressBook = {
  [LOADING]: {
    [GET]: addressBookAction(GET, LOADING),
    [UPDATE]: addressBookAction(UPDATE, LOADING),
    [CREATE]: addressBookAction(CREATE, LOADING),
    [DELETE]: addressBookAction(DELETE, LOADING),
  },
  [VERIFY]: {
    [UPDATE]: addressBookAction(VERIFY, SUCCESS),
    [CREATE]: addressBookAction(VERIFY, SUCCESS),
  },
  [SUCCESS]: {
    [GET]: addressBookAction(GET, SUCCESS),
    [UPDATE]: addressBookAction(UPDATE, SUCCESS),
    [CREATE]: addressBookAction(CREATE, SUCCESS),
    [DELETE]: addressBookAction(DELETE, SUCCESS),
  },
  [FAILURE]: {
    [GET]: addressBookAction(GET, FAILURE),
    [UPDATE]: addressBookAction(UPDATE, FAILURE),
    [CREATE]: addressBookAction(CREATE, FAILURE),
    [DELETE]: addressBookAction(DELETE, FAILURE),
  },
}

const getAddressBookActions = (method): any => {
  const {
    [LOADING]: { [method]: loading },
    [VERIFY]: { [method]: verify },
    [SUCCESS]: { [method]: success },
    [FAILURE]: { [method]: failure },
  } = addressBook
  return { loading, verify, success, failure }
}

const settingsUpdateFailure = [
  triggerSettingsUpdateFailMessage,
  updateDigitalReceiptsPreferencesFail,
  updateNewsletterPreferencesFail,
  requestCustomerDataFail,
  triggerAccountAddFailMessage,
]

const settingsUpdateSuccess = [
  triggerSettingsUpdateSuccessMessage,
  updateDigitalReceiptsPreferencesSuccess,
  updateNewsletterPreferencesSuccess,
  requestCustomerDataSuccess,
  triggerAccountAddSuccessMessage,
]

const settingsDeleteAccountFailure = [triggerSettingsDeleteFailMessage]

// Payments

const fetchPaymentsStart = createAction('PAYMENTS/fetch payments start')
const fetchPaymentsSuccess = createAction<any>('PAYMENTS/fetch payments success')
const fetchPaymentsFail = createAction<Error>('PAYMENTS/fetch payments fail')

const removeCardStart = createAction('PAYMENTS/remove card start')
const removeCardSuccess = createAction('PAYMENTS/remove card success')
const removeCardFail = createAction<{
  error: Error
  previousPaymentsState: UserSettingsType['payments']
}>('PAYMENTS/remove card fail')

const removeBankAccountStart = createAction('PAYMENTS/remove bank account start')
const removeBankAccountSuccess = createAction('PAYMENTS/remove bank account success')
const removeBankAccountFail = createAction<{
  error: Error
  previousPaymentsState: UserSettingsType['payments']
}>('PAYMENTS/remove bank account fail')

const fetchBranchStart = createAction('fetch branch start')
const fetchBranchSuccess = createAction<{ branchList: BranchResponseItem }>('fetch branch success')
const fetchBranchFail = createAction<Error>('fetch branch fail')
const setDefaultCardStart = createAction<string>('PAYMENTS/set default card start')
const setDefaultCardSuccess = createAction<string>('PAYMENTS/set default card success')
const setDefaultCardFail = createAction<{
  error: Error
  previousPaymentsState: UserSettingsType['payments']
}>('PAYMENTS/set default card fail')

const triggerNotification = createAction<NotificationType>('PAYMENTS/Trigger success notification')

const loadCardStoreIds = createAction<PaymentCard[]>('PAYMENTS/load card store IDs')

const addPaymentCardMemberIdStart = createAction<string>('PAYMENTS/add card store ID start')
const addPaymentCardMemberIdSuccess = createAction<string>('PAYMENTS/add card store ID success')
const addPaymentCardMemberIdFail = createAction<{ alias: string; errorMessage?: string }>(
  'PAYMENTS/add card store ID fail',
)

const deletePaymentCardMemberIdStart = createAction<string>('PAYMENTS/delete card store ID start')
const deletePaymentCardMemberIdSuccess = createAction<string>(
  'PAYMENTS/delete card store ID success',
)
const deletePaymentCardMemberIdFail = createAction<string>('PAYMENTS/delete card store ID fail')

const addBankAccountStart = createAction('PAYMENTS/ add bank account start')
const addBankAccountSuccess = createAction<any>('PAYMENTS/ add bank account success')
const addBankAccountFail = createAction<{ error: Error; previousState: UserSettingsType }>(
  'PAYMENTS/ add bank account fail',
)
const updateBankAccountDetailsFail = createAction<any>('settings/update bank account details fail')
const addNewPaymentCardStart = createAction('PAYMENTS/add new card start')
const addNewPaymentCardSuccess = createAction('PAYMENTS/add new card success')
const addNewPaymentCardFail = createAction<UserSettingsType['payments']['addNewCard']['errors']>(
  'PAYMENTS/add new card fail',
)
const updateBankAccountErrorState = createAction('settings/update bank account error state')
const resetNewPaymentCardForm = createAction('PAYMENTS/reset new card form')
// End Payments

const setNewsSubscription = createAction<UserSettingsType['newsLetterSubscription']>(
  'set news Subscription state',
)

export default {
  settingsUpdateFailure,
  settingsUpdateSuccess,
  resendEmailConfirmationSuccess,
  resendEmailConfirmationFail,
  resendEmailConfirmationReset,
  updateDigitalReceiptsPreferencesSuccess,
  updateDigitalReceiptsPreferencesFail,
  updateNewsletterPreferencesSuccess,
  updateNewsletterPreferencesFail,
  settingsUpdateMessageReset,
  deactivatePayLaterStart,
  deactivatePayLaterSuccess,
  deactivatePayLaterFail,
  requestCustomerDataSuccess,
  requestCustomerDataFail,
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFail,
  changePasswordResetErrors,
  triggerSettingsUpdateSuccessMessage,
  triggerSettingsUpdateFailMessage,
  deleteAccountStart,
  deleteAccountSuccess,
  deleteAccountFail,
  settingsDeleteAccountFailure,
  triggerSettingsDeleteFailMessage,
  updateUserDetailsStart,
  updateUserDetailsSuccess,
  updateUserDetailsFail,
  // Community nickname
  resetCommunityNickname,
  updateCommunityNicknameStart,
  updateCommunityNicknameSuccess,
  updateCommunityNicknameFail,
  // End Community nickname
  getAltIdsStart,
  getAltIdsSuccess,
  getAltIdsFail,
  addAltIdStart,
  addAltIdSuccess,
  addAltIdFail,
  deleteAltIdStart,
  deleteAltIdSuccess,
  deleteAltIdFail,
  setApiErrorState,
  addressBook,
  getAddressBookActions,
  settingsDeleteMessageReset,
  setSelectedProvince,
  setSelectedCity,
  fetchCitiesStart,
  fetchCitiesSuccess,
  fetchCitiesFail,
  fetchDistrictsStart,
  fetchDistrictsSuccess,
  fetchDistrictsFail,
  fetchPostalCodeStart,
  fetchPostalCodeSuccess,
  fetchPostalCodeFail,
  // payments
  fetchPaymentsStart,
  fetchPaymentsSuccess,
  fetchPaymentsFail,
  removeCardStart,
  removeCardSuccess,
  removeCardFail,
  setDefaultCardStart,
  setDefaultCardSuccess,
  setDefaultCardFail,
  triggerNotification,
  loadCardStoreIds,
  addPaymentCardMemberIdStart,
  addPaymentCardMemberIdSuccess,
  addPaymentCardMemberIdFail,
  deletePaymentCardMemberIdStart,
  deletePaymentCardMemberIdSuccess,
  deletePaymentCardMemberIdFail,
  addBankAccountStart,
  addBankAccountSuccess,
  addBankAccountFail,
  triggerAccountAddSuccessMessage,
  triggerAccountAddFailMessage,
  removeBankAccountStart,
  removeBankAccountSuccess,
  removeBankAccountFail,
  updateBankAccountDetailsFail,
  updateBankAccountErrorState,
  addNewPaymentCardStart,
  addNewPaymentCardSuccess,
  addNewPaymentCardFail,
  resetNewPaymentCardForm,
  fetchBranchStart,
  fetchBranchSuccess,
  fetchBranchFail,
  // End payments
  addPhoneAsAltIdSuccess,
  removePhoneAsAltIdSuccess,
  resetPhoneAsAltIdNotification,
  setNewsSubscription,
}
